import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const globalStyle = `
    html, body {
        background: #0a0e1a !important;
        min-height: 100vh;
        margin: 0;
        padding: 0;
    }
`;

const logo = '/logo.jpg';

export default function Configuration() {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddUser, setShowAddUser] = useState(false);
    const [showEditUser, setShowEditUser] = useState(false);
    const [currentUser, setCurrentUser] = useState({});
    const [newUser, setNewUser] = useState({
        username: '',
        password: '',
        role: 'user',
        two_factor_enabled: false
    });
    const [editingUser, setEditingUser] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    function buildUrl(path) {
        const base = (process.env.REACT_APP_API_URL || "http://localhost:5050").trim();
        if (!base) return path.startsWith("/") ? path : `/${path}`;
        return `${base.replace(/\/+$/, "")}/${path.replace(/^\/+/, "")}`;
    }

    function getToken() {
        return localStorage.getItem('token') || localStorage.getItem('authToken');
    }

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        setCurrentUser(user);

        if (!user.id || user.role !== 'admin') {
            navigate('/dashboard');
            return;
        }

        loadUsers();
    }, [navigate]);

    const loadUsers = async () => {
        try {
            const token = getToken();
            if (!token) {
                setError('Token d\'authentification manquant');
                setLoading(false);
                return;
            }

            const url = buildUrl('/api/users');
            console.log('[CONFIG] loadUsers -> GET', url); // <-- debug
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                if (response.status === 401) {
                    setError('Session expirée');
                    setTimeout(() => navigate('/login'), 2000);
                    return;
                } else if (response.status === 403) {
                    setError('Accès refusé');
                    setTimeout(() => navigate('/dashboard'), 2000);
                    return;
                }
                throw new Error(`Erreur ${response.status}`);
            }

            const data = await response.json();
            setUsers(data.users || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const token = getToken();
            const url = buildUrl('/api/users/create');
            console.log('[CONFIG] handleAddUser -> POST', url, newUser); // <-- debug
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(newUser)
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Erreur');

            setSuccess('✓ Utilisateur créé');
            setNewUser({ username: '', password: '', role: 'user', two_factor_enabled: false });
            setShowAddUser(false);
            loadUsers();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleEditUser = (user) => {
        setEditingUser({
            id: user.id,
            username: user.username,
            role: user.role,
            password: ''
        });
        setShowEditUser(true);
    };

    const handleUpdateUser = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');

        try {
            const token = getToken();
            const updateData = {
                username: editingUser.username,
                role: editingUser.role
            };
            if (editingUser.password.trim()) updateData.password = editingUser.password;

            const url = buildUrl(`/api/users/${editingUser.id}`);
            console.log('[CONFIG] handleUpdateUser -> PUT', url, updateData); // <-- debug
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(updateData)
            });

            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'Erreur');

            setSuccess('✓ Utilisateur modifié');
            setEditingUser(null);
            setShowEditUser(false);
            loadUsers();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDeleteUser = async (userId) => {
        if (!window.confirm('Supprimer cet utilisateur ?')) return;

        try {
            const token = getToken();
            const url = buildUrl(`/api/users/${userId}`);
            console.log('[CONFIG] handleDeleteUser -> DELETE', url); // <-- debug
            const response = await fetch(url, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) throw new Error('Erreur');

            setSuccess('✓ Utilisateur supprimé');
            loadUsers();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        }
    };

    const handleToggle2FA = async (userId, enabled) => {
        try {
            const token = getToken();
            const url = buildUrl(`/api/users/${userId}/2fa`);
            console.log('[CONFIG] handleToggle2FA -> PUT', url, { enabled }); // <-- debug
            const response = await fetch(url, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ enabled })
            });

            if (!response.ok) throw new Error('Erreur');

            setSuccess('✓ 2FA modifié');
            loadUsers();
            setTimeout(() => setSuccess(''), 3000);
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#0a0e1a', color: '#fff', fontSize: 18, flexDirection: 'column', gap: 16 }}>
                <div style={{ fontSize: 32 }}>⚙️</div>
                <div>Chargement...</div>
            </div>
        );
    }

    return (
        <>
            <style>{globalStyle}</style>
            <div style={{ minHeight: '100vh', background: '#0a0e1a', color: '#fff', fontFamily: "'Inter', -apple-system, sans-serif" }}>
                {/* Header */}
                <header style={{
                    background: 'linear-gradient(135deg, #1a1f35 0%, #0f1420 100%)',
                    borderBottom: '1px solid rgba(99, 102, 241, 0.1)',
                    padding: '16px 32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.4)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img src={logo} alt="Logo" style={{ width: 36, height: 36, borderRadius: 8 }} />
                        <span style={{ fontWeight: 700, fontSize: 22, background: 'linear-gradient(135deg, #6366f1, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Prospectys</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <button onClick={() => navigate('/dashboard')} style={{ background: 'rgba(99, 102, 241, 0.1)', color: '#a78bfa', border: '1px solid rgba(99, 102, 241, 0.2)', borderRadius: 8, padding: '8px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            ← Dashboard
                        </button>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'rgba(99, 102, 241, 0.1)', borderRadius: 10, padding: '6px 14px', border: '1px solid rgba(99, 102, 241, 0.2)' }}>
                            <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #a855f7)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14 }}>
                                {currentUser.username?.[0]?.toUpperCase() || 'A'}
                            </div>
                            <span style={{ fontSize: 14, fontWeight: 600 }}>{currentUser.username}</span>
                            <span style={{ fontSize: 11, fontWeight: 700, background: 'rgba(139, 92, 246, 0.2)', color: '#a78bfa', padding: '2px 8px', borderRadius: 6 }}>ADMIN</span>
                        </div>
                    </div>
                </header>

                {/* Main content */}
                <main style={{ maxWidth: 1400, margin: '0 auto', padding: '32px 24px' }}>
                    <div style={{ marginBottom: 32 }}>
                        <h1 style={{ fontSize: 32, fontWeight: 800, color: '#a78bfa', marginBottom: 8 }}>⚙️ Configuration</h1>
                        <p style={{ fontSize: 14, color: '#6b7280' }}>Gérez les utilisateurs et les paramètres de l'application</p>
                    </div>

                    {/* Messages */}
                    {error && (
                        <div style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#f87171', padding: 14, borderRadius: 10, marginBottom: 24, fontSize: 14, fontWeight: 600 }}>
                            ⚠️ {error}
                        </div>
                    )}
                    {success && (
                        <div style={{ background: 'rgba(16, 185, 129, 0.15)', border: '1px solid rgba(16, 185, 129, 0.3)', color: '#34d399', padding: 14, borderRadius: 10, marginBottom: 24, fontSize: 14, fontWeight: 600 }}>
                            {success}
                        </div>
                    )}

                    {/* Section utilisateurs */}
                    <div style={{ background: 'linear-gradient(180deg, #0f1420 0%, #1a1f35 100%)', borderRadius: 16, border: '1px solid rgba(99, 102, 241, 0.1)', overflow: 'hidden' }}>
                        <div style={{ padding: '24px', borderBottom: '1px solid rgba(99, 102, 241, 0.1)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                                <h2 style={{ fontSize: 20, fontWeight: 700, color: '#e5e7eb', marginBottom: 4 }}>👥 Utilisateurs</h2>
                                <p style={{ fontSize: 13, color: '#6b7280' }}>{users.length} utilisateur{users.length > 1 ? 's' : ''} enregistré{users.length > 1 ? 's' : ''}</p>
                            </div>
                            <button onClick={() => setShowAddUser(true)} style={{ background: 'linear-gradient(135deg, #6366f1, #a855f7)', color: '#fff', border: 'none', borderRadius: 10, padding: '10px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)' }}>
                                ➕ Ajouter
                            </button>
                        </div>

                        {/* Table header */}
                        <div style={{ display: 'grid', gridTemplateColumns: '2fr 120px 120px 150px 180px', gap: 16, padding: '16px 24px', background: 'rgba(15, 20, 32, 0.5)', fontSize: 12, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            <div>Utilisateur</div>
                            <div>Rôle</div>
                            <div>2FA</div>
                            <div>Créé le</div>
                            <div>Actions</div>
                        </div>

                        {/* Users list */}
                        {users.map((user, idx) => (
                            <div key={user.id} style={{ display: 'grid', gridTemplateColumns: '2fr 120px 120px 150px 180px', gap: 16, padding: '20px 24px', borderBottom: idx < users.length - 1 ? '1px solid rgba(99, 102, 241, 0.05)' : 'none', alignItems: 'center', transition: 'background 0.2s' }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 15, color: '#e5e7eb', marginBottom: 4 }}>{user.username}</div>
                                    <div style={{ fontSize: 12, color: '#6b7280' }}>ID: {user.id}</div>
                                </div>
                                <div>
                                    <span style={{
                                        background: user.role === 'admin' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(99, 102, 241, 0.15)',
                                        color: user.role === 'admin' ? '#f87171' : '#a78bfa',
                                        border: `1px solid ${user.role === 'admin' ? 'rgba(239, 68, 68, 0.3)' : 'rgba(99, 102, 241, 0.3)'}`,
                                        padding: '4px 10px',
                                        borderRadius: 6,
                                        fontSize: 12,
                                        fontWeight: 700,
                                        textTransform: 'uppercase'
                                    }}>
                                        {user.role}
                                    </span>
                                </div>
                                <div>
                                    <button onClick={() => handleToggle2FA(user.id, !user.two_factor_enabled)} style={{
                                        background: user.two_factor_enabled ? 'rgba(16, 185, 129, 0.15)' : 'rgba(107, 114, 128, 0.15)',
                                        color: user.two_factor_enabled ? '#34d399' : '#9ca3af',
                                        border: `1px solid ${user.two_factor_enabled ? 'rgba(16, 185, 129, 0.3)' : 'rgba(107, 114, 128, 0.3)'}`,
                                        borderRadius: 6,
                                        padding: '4px 10px',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        cursor: 'pointer',
                                        transition: 'all 0.2s'
                                    }}>
                                        {user.two_factor_enabled ? '✓ ON' : '✕ OFF'}
                                    </button>
                                </div>
                                <div style={{ fontSize: 13, color: '#9ca3af' }}>
                                    {new Date(user.created_at).toLocaleDateString('fr-FR')}
                                </div>
                                <div style={{ display: 'flex', gap: 8 }}>
                                    <button onClick={() => handleEditUser(user)} style={{ background: 'rgba(251, 146, 60, 0.15)', color: '#fb923c', border: '1px solid rgba(251, 146, 60, 0.3)', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                                        ✏️ Modifier
                                    </button>
                                    {user.id !== currentUser.id && (
                                        <button onClick={() => handleDeleteUser(user.id)} style={{ background: 'rgba(239, 68, 68, 0.15)', color: '#f87171', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 6, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', transition: 'all 0.2s' }}>
                                            🗑️
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}

                        {users.length === 0 && (
                            <div style={{ padding: 60, textAlign: 'center', color: '#6b7280' }}>
                                <div style={{ fontSize: 48, marginBottom: 16 }}>👥</div>
                                <div style={{ fontWeight: 600, marginBottom: 8 }}>Aucun utilisateur</div>
                                <div style={{ fontSize: 13 }}>Ajoutez votre premier utilisateur</div>
                            </div>
                        )}
                    </div>
                </main>

                {/* Modal Add User */}
                {showAddUser && (
                    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                        <div style={{ background: 'linear-gradient(135deg, #1a1f35 0%, #0f1420 100%)', border: '1px solid rgba(99, 102, 241, 0.2)', borderRadius: 16, padding: 32, width: '100%', maxWidth: 450, boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)' }}>
                            <h3 style={{ fontSize: 22, fontWeight: 700, color: '#a78bfa', marginBottom: 24 }}>➕ Ajouter un utilisateur</h3>
                            <form onSubmit={handleAddUser}>
                                <div style={{ marginBottom: 20 }}>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nom d'utilisateur</label>
                                    <input type="text" value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} required style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(15, 20, 32, 0.8)', color: '#fff', fontSize: 14, outline: 'none' }} />
                                </div>
                                <div style={{ marginBottom: 20 }}>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Mot de passe</label>
                                    <input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(15, 20, 32, 0.8)', color: '#fff', fontSize: 14, outline: 'none' }} />
                                </div>
                                <div style={{ marginBottom: 24 }}>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rôle</label>
                                    <select value={newUser.role} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(15, 20, 32, 0.8)', color: '#fff', fontSize: 14, outline: 'none' }}>
                                        <option value="user">Utilisateur</option>
                                        <option value="admin">Administrateur</option>
                                    </select>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <button type="button" onClick={() => setShowAddUser(false)} style={{ flex: 1, background: 'rgba(107, 114, 128, 0.15)', color: '#9ca3af', border: '1px solid rgba(107, 114, 128, 0.3)', borderRadius: 8, padding: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                                        Annuler
                                    </button>
                                    <button type="submit" style={{ flex: 1, background: 'linear-gradient(135deg, #6366f1, #a855f7)', color: '#fff', border: 'none', borderRadius: 8, padding: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)' }}>
                                        Créer
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* Modal Edit User */}
                {showEditUser && editingUser && (
                    <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
                        <div style={{ background: 'linear-gradient(135deg, #1a1f35 0%, #0f1420 100%)', border: '1px solid rgba(99, 102, 241, 0.2)', borderRadius: 16, padding: 32, width: '100%', maxWidth: 450, boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)' }}>
                            <h3 style={{ fontSize: 22, fontWeight: 700, color: '#a78bfa', marginBottom: 24 }}>✏️ Modifier l'utilisateur</h3>
                            <form onSubmit={handleUpdateUser}>
                                <div style={{ marginBottom: 20 }}>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nom d'utilisateur</label>
                                    <input type="text" value={editingUser.username} onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })} required style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(15, 20, 32, 0.8)', color: '#fff', fontSize: 14, outline: 'none' }} />
                                </div>
                                <div style={{ marginBottom: 20 }}>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Nouveau mot de passe (optionnel)</label>
                                    <input type="password" value={editingUser.password} onChange={(e) => setEditingUser({ ...editingUser, password: e.target.value })} placeholder="Laisser vide si inchangé" style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(15, 20, 32, 0.8)', color: '#fff', fontSize: 14, outline: 'none' }} />
                                </div>
                                <div style={{ marginBottom: 24 }}>
                                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#9ca3af', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Rôle</label>
                                    <select value={editingUser.role} onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })} style={{ width: '100%', padding: 12, borderRadius: 8, border: '1px solid rgba(99, 102, 241, 0.2)', background: 'rgba(15, 20, 32, 0.8)', color: '#fff', fontSize: 14, outline: 'none' }}>
                                        <option value="user">Utilisateur</option>
                                        <option value="admin">Administrateur</option>
                                    </select>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <button type="button" onClick={() => { setShowEditUser(false); setEditingUser(null); }} style={{ flex: 1, background: 'rgba(107, 114, 128, 0.15)', color: '#9ca3af', border: '1px solid rgba(107, 114, 128, 0.3)', borderRadius: 8, padding: 12, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                                        Annuler
                                    </button>
                                    <button type="submit" style={{ flex: 1, background: 'linear-gradient(135deg, #fb923c, #f59e0b)', color: '#fff', border: 'none', borderRadius: 8, padding: 12, fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: '0 4px 12px rgba(251, 146, 60, 0.3)' }}>
                                        Modifier
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}